"use client";

import type React from "react";
import {
  OrderStatus,
  OrderPaymentStatus,
  type OrderOutput,
  type CustomersOutput,
  orderStatusTranslation,
  orderPaymentStatusTranslation,
  orderDeliveryTypeTransalation,
  type TagsOutput,
} from "@/interfaces";
import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Trash2, Calendar, CreditCard, Truck, User, Tag } from "lucide-react";
import { numberToMoney } from "@/util/moneyConversion";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface OrderListProps {
  orders: OrderOutput[];
  onDelete: (id: string) => void;
  onSelect: (id: string) => void;
}

export function OrderList({
  orders,
  onDelete,
  onSelect,
}: Readonly<OrderListProps>) {
  const getStatusColor = (status: OrderStatus) => {
    switch (status) {
      case OrderStatus.PENDING:
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case OrderStatus.SCHEDULED:
        return "bg-blue-100 text-blue-800 border-blue-200";
      case OrderStatus.IN_PREPARATION:
        return "bg-orange-100 text-orange-800 border-orange-200";
      case OrderStatus.PREPARED:
        return "bg-teal-100 text-teal-800 border-teal-200";
      case OrderStatus.DONE:
        return "bg-green-100 text-green-800 border-green-200";
      case OrderStatus.CANCELED:
        return "bg-red-100 text-red-800 border-red-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getPaymentStatusColor = (status: OrderPaymentStatus) => {
    switch (status) {
      case OrderPaymentStatus.PAID:
        return "bg-green-100 text-green-800 border-green-200";
      case OrderPaymentStatus.PENDING:
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case OrderPaymentStatus.PARTIALLY_PAID:
        return "bg-orange-100 text-orange-800 border-orange-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const handleDelete = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    onDelete(id);
  };

  return (
    <div className="bg-white dark:bg-gray-950 rounded-md border border-gray-200 dark:border-gray-800 overflow-hidden">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50 dark:bg-gray-900/50">
              <TableHead className="w-[250px]">Cliente</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Pagamento</TableHead>
              <TableHead>Entrega</TableHead>
              <TableHead>Data de Preparação</TableHead>
              <TableHead>Valor</TableHead>
              <TableHead>Tags</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orders?.map((order) => (
              <TableRow
                key={order.id}
                className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900/30"
                onClick={() => onSelect(order.id)}
              >
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-gray-400" />
                    <span className="truncate max-w-[200px]">
                      {order.customer
                        ? (order.customer as CustomersOutput)?.name
                        : `Pedido #${order.id.substring(0, 8)}`}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge
                    className={`${getStatusColor(
                      order.status
                    )} border px-2 py-0.5 text-xs font-medium`}
                  >
                    {orderStatusTranslation[order.status]}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge
                    className={`${getPaymentStatusColor(
                      order.paymentStatus
                    )} border px-2 py-0.5 text-xs font-medium`}
                  >
                    {orderPaymentStatusTranslation[order.paymentStatus]}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1.5">
                    <Truck className="h-3.5 w-3.5 text-gray-500" />
                    <span className="text-sm">
                      {
                        orderDeliveryTypeTransalation[
                          order.delivery.deliveryType
                        ]
                      }
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1.5">
                    <Calendar className="h-3.5 w-3.5 text-gray-500" />
                    <span className="text-sm">
                      {format(new Date(order.preparationDate), "dd/MM/yyyy")}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1.5">
                    <CreditCard className="h-3.5 w-3.5 text-gray-500" />
                    <span className="text-sm font-medium text-green-700 dark:text-green-500">
                      {numberToMoney(order.totalAmount || 0)}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1 max-w-[150px]">
                    {order.tags.length > 0 ? (
                      order.tags.length <= 2 ? (
                        (order.tags as TagsOutput[]).map((tag) => (
                          <Badge
                            key={tag.id}
                            style={{
                              backgroundColor: tag.styling?.primaryColor,
                              color: tag.styling?.fontColor,
                              border: `1px solid ${tag.styling?.secondaryColor}`,
                            }}
                            className="text-[10px] px-1.5 py-0 rounded"
                            variant="outline"
                          >
                            {tag.name}
                          </Badge>
                        ))
                      ) : (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="flex items-center gap-1">
                                <Tag className="h-3.5 w-3.5 text-gray-500" />
                                <span className="text-sm text-gray-500">
                                  {order.tags.length} tags
                                </span>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              <div className="flex flex-wrap gap-1 max-w-[200px]">
                                {(order.tags as TagsOutput[]).map((tag) => (
                                  <Badge
                                    key={tag.id}
                                    style={{
                                      backgroundColor:
                                        tag.styling?.primaryColor,
                                      color: tag.styling?.fontColor,
                                      border: `1px solid ${tag.styling?.secondaryColor}`,
                                    }}
                                    className="text-[10px] px-1.5 py-0 rounded"
                                    variant="outline"
                                  >
                                    {tag.name}
                                  </Badge>
                                ))}
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )
                    ) : (
                      <span className="text-sm text-gray-400">-</span>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 rounded-full text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/50"
                    onClick={(e) => handleDelete(e, order.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                    <span className="sr-only">Excluir pedido</span>
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
